import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Divider,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile,
  PictureAsPdf,
  Image as ImageIcon,
  DeleteOutline,
} from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { canDelete } from '../../utils/permissions';

export default function Manutencoes() {
  const { usuario } = useAuth();
  const [manutencoes, setManutencoes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedVeiculo, setSelectedVeiculo] = useState(null);
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [arquivosSalvos, setArquivosSalvos] = useState([]);
  const [totalGastos, setTotalGastos] = useState(0);
  const podeExcluir = canDelete(usuario);

  const [formData, setFormData] = useState({
    veiculo_id: null,
    tipo: 'mecanica',
    descricao: '',
    valor: '',
    data_manutencao: new Date().toISOString().split('T')[0],
    oficina: '',
    observacoes: '',
  });

  useEffect(() => {
    loadManutencoes();
    loadVeiculos();
  }, []);

  const loadManutencoes = async () => {
    try {
      const response = await api.get('/manutencoes', { params: { limit: 1000 } });
      const data = response.data.manutencoes || response.data || [];
      setManutencoes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar manutenções:', error);
      toast.error('Erro ao carregar manutenções');
      setManutencoes([]);
    }
  };

  const loadVeiculos = async () => {
    try {
      const response = await api.get('/veiculos', { params: { limit: 1000 } });
      const data = response.data.veiculos || response.data || [];
      setVeiculos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
      setVeiculos([]);
    }
  };

  const calcularTotalGastos = (veiculoId) => {
    const gastosVeiculo = manutencoes.filter((m) => m.veiculo_id === veiculoId);
    const total = gastosVeiculo.reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
    setTotalGastos(total);
  };

  const openArquivo = (arquivo) => {
    if (!arquivo?.url) return;
    const fullUrl = arquivo.url.startsWith('http') ? arquivo.url : `${window.location.origin}${arquivo.url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const loadArquivosManutencao = async (manutencaoId) => {
    try {
      const response = await api.get(`/manutencoes/${manutencaoId}/arquivos`);
      setArquivosSalvos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar anexos da manutenção:', error);
      setArquivosSalvos([]);
    }
  };

  const handleDeleteArquivoSalvo = async (arquivoId) => {
    if (!editingId) return;
    try {
      await api.delete(`/manutencoes/${editingId}/arquivos/${arquivoId}`);
      setArquivosSalvos((prev) => prev.filter((arquivo) => arquivo.id !== arquivoId));
      toast.success('Anexo removido com sucesso');
    } catch (error) {
      console.error('Erro ao remover anexo da manutenção:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover anexo');
    }
  };

  const handleOpen = (manutencao = null) => {
    if (manutencao) {
      setEditingId(manutencao.id);
      setFormData({
        veiculo_id: manutencao.veiculo_id,
        tipo: manutencao.tipo,
        descricao: manutencao.descricao,
        valor: manutencao.valor,
        data_manutencao: manutencao.data_manutencao.split('T')[0],
        oficina: manutencao.oficina || '',
        observacoes: manutencao.observacoes || '',
      });
      const veiculo = veiculos.find((v) => v.id === manutencao.veiculo_id);
      setSelectedVeiculo(veiculo || null);
      calcularTotalGastos(manutencao.veiculo_id);
      setArquivosSalvos(manutencao.arquivos || []);
      if (manutencao.id) {
        loadArquivosManutencao(manutencao.id);
      }
    } else {
      setEditingId(null);
      setFormData({
        veiculo_id: null,
        tipo: 'mecanica',
        descricao: '',
        valor: '',
        data_manutencao: new Date().toISOString().split('T')[0],
        oficina: '',
        observacoes: '',
      });
      setSelectedVeiculo(null);
      setTotalGastos(0);
      setArquivosSalvos([]);
    }
    setNotasFiscais([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setSelectedVeiculo(null);
    setNotasFiscais([]);
    setTotalGastos(0);
    setArquivosSalvos([]);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      const isValidType = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
        file.type
      );
      if (!isValidType) {
        toast.error(`Arquivo ${file.name} não é PDF, PNG ou JPG`);
      }
      return isValidType;
    });

    setNotasFiscais([...notasFiscais, ...validFiles]);
    if (validFiles.length > 0) {
      toast.success(`${validFiles.length} arquivo(s) adicionado(s)`);
    }
  };

  const handleRemoveFile = (index) => {
    const newFiles = notasFiscais.filter((_, i) => i !== index);
    setNotasFiscais(newFiles);
    toast.info('Arquivo removido');
  };

  const handleSubmit = async () => {
    try {
      if (!formData.veiculo_id || !formData.descricao || !formData.valor) {
        toast.error('Selecione um veículo, preencha a descrição e o valor');
        return;
      }

      const dataToSend = {
        ...formData,
        valor: parseFloat(formData.valor),
      };

      let manutencaoId = editingId;
      if (editingId) {
        await api.put(`/manutencoes/${editingId}`, dataToSend);
        toast.success('Manutenção atualizada com sucesso!');
      } else {
        const response = await api.post('/manutencoes', dataToSend);
        manutencaoId = response.data.id;
        toast.success('Manutenção registrada com sucesso!');
      }

      if (notasFiscais.length > 0) {
        let enviados = 0;
        for (const arquivo of notasFiscais) {
          const formDataArquivo = new FormData();
          formDataArquivo.append('file', arquivo);
          await api.post(`/manutencoes/${manutencaoId}/arquivos`, formDataArquivo, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          enviados += 1;
        }
        toast.success(`${enviados} anexo(s) salvo(s) na manutenção`);
      }

      handleClose();
      loadManutencoes();
    } catch (error) {
      console.error('Erro ao salvar manutenção:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar manutenção');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir esta manutenção')) {
      try {
        await api.delete(`/manutencoes/${id}`);
        toast.success('Manutenção excluída com sucesso!');
        loadManutencoes();
      } catch (error) {
        console.error('Erro ao excluir manutenção:', error);
        toast.error('Erro ao excluir manutenção');
      }
    }
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      mecanica: 'Mecânica',
      eletrica: 'Elétrica',
      funilaria: 'Funilaria',
      pintura: 'Pintura',
      revisao: 'Revisão',
      limpeza: 'Limpeza',
      outros: 'Outros',
    };
    return tipos[tipo] || tipo;
  };

  const getTipoColor = (tipo) => {
    const cores = {
      mecanica: 'error',
      eletrica: 'warning',
      funilaria: 'info',
      pintura: 'secondary',
      revisao: 'primary',
      limpeza: 'success',
      outros: 'default',
    };
    return cores[tipo] || 'default';
  };

  const getFileIcon = (file) => {
    if ((file.type || file.mime_type) === 'application/pdf') {
      return <PictureAsPdf color="error" />;
    }
    return <ImageIcon color="primary" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Manutenções e Despesas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Nova Manutenção
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Veículo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Oficina</TableCell>
              <TableCell>Anexos</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {manutencoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma manutenção registrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              manutencoes.map((manutencao) => (
                <TableRow key={manutencao.id}>
                  <TableCell>
                    {manutencao.veiculo
                      ? `${manutencao.veiculo.marca} ${manutencao.veiculo.modelo} - ${manutencao.veiculo.placa}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTipoLabel(manutencao.tipo)}
                      color={getTipoColor(manutencao.tipo)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{manutencao.descricao}</TableCell>
                  <TableCell>R$ {parseFloat(manutencao.valor || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    {new Date(manutencao.data_manutencao).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>{manutencao.oficina || '-'}</TableCell>
                  <TableCell>
                    {manutencao.arquivos?.length ? (
                      <Chip
                        label={`${manutencao.arquivos.length} arquivo(s)`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpen(manutencao)}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpen(manutencao)}
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                    {podeExcluir && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(manutencao.id)}
                        title="Excluir"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Nova/Editar Manutenção */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar Manutenção' : 'Nova Manutenção'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Seleção de Veículo */}
            <Grid item xs={12}>
              <Autocomplete
                options={veiculos}
                getOptionLabel={(option) =>
                  `${option.marca} ${option.modelo} - ${option.placa} (${option.ano_modelo})`
                }
                value={selectedVeiculo}
                onChange={(event, newValue) => {
                  setSelectedVeiculo(newValue);
                  setFormData({ ...formData, veiculo_id: newValue?.id || null });
                  if (newValue) {
                    calcularTotalGastos(newValue.id);
                  } else {
                    setTotalGastos(0);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Veículo *" fullWidth />}
              />
            </Grid>

            {/* Card com Total de Gastos do Veículo */}
            {selectedVeiculo && totalGastos > 0 && (
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#fff3e0' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary">
                      Total de Despesas deste Veículo
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      R$ {totalGastos.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Tipo de Manutenção */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Manutenção"
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              >
                <MenuItem value="mecanica">Mecânica</MenuItem>
                <MenuItem value="eletrica">Elétrica</MenuItem>
                <MenuItem value="funilaria">Funilaria</MenuItem>
                <MenuItem value="pintura">Pintura</MenuItem>
                <MenuItem value="revisao">Revisão</MenuItem>
                <MenuItem value="limpeza">Limpeza</MenuItem>
                <MenuItem value="outros">Outros</MenuItem>
              </TextField>
            </Grid>

            {/* Data da Manutenção */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data da Manutenção"
                type="date"
                value={formData.data_manutencao}
                onChange={(e) => setFormData({ ...formData, data_manutencao: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Descrição do Serviço"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Troca de óleo e filtro, Reparo no motor, etc."
              />
            </Grid>

            {/* Valor */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Valor (R$)"
                type="number"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>

            {/* Oficina */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Oficina/Prestador"
                value={formData.oficina}
                onChange={(e) => setFormData({ ...formData, oficina: e.target.value })}
                placeholder="Nome da oficina ou prestador de serviço"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Upload de Nota Fiscal */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Anexar Nota Fiscal / Orçamento
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <input
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                id="upload-nota-fiscal"
                multiple
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-nota-fiscal">
                <Button variant="outlined" component="span" startIcon={<AttachFile />}>
                  Importar Nota de Serviço
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Os arquivos enviados ficam salvos no servidor e podem ser consultados pela própria manutenção.
              </Typography>
            </Grid>

            {arquivosSalvos.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Anexos Salvos ({arquivosSalvos.length})
                  </Typography>
                  <List dense>
                    {arquivosSalvos.map((arquivo) => (
                      <ListItem
                        key={arquivo.id}
                        onClick={() => openArquivo(arquivo)}
                        sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        {getFileIcon(arquivo)}
                        <ListItemText
                          primary={arquivo.nome_original}
                          secondary={formatFileSize(arquivo.tamanho)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleDeleteArquivoSalvo(arquivo.id)}>
                            <DeleteOutline />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            {notasFiscais.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Arquivos Anexados ({notasFiscais.length})
                  </Typography>
                  <List dense>
                    {notasFiscais.map((file, index) => (
                      <ListItem key={index}>
                        {getFileIcon(file)}
                        <ListItemText
                          primary={file.name}
                          secondary={formatFileSize(file.size)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                            <DeleteOutline />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais sobre a manutenção..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Atualizar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

